import { Toaster } from "@/components/ui/toaster";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";


import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Ban } from "lucide-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useMaintenanceMode } from "@/hooks/useMaintenanceMode";
import MaintenancePage from "@/pages/MaintenancePage";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { DemoProvider } from "./contexts/DemoContext";
import { CartProvider } from "./contexts/CartContext";
import { WishlistProvider } from "./contexts/WishlistContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ConfirmationProvider } from "./contexts/ConfirmationContext";
import { lazy, Suspense } from "react";
import { LazyMotion, domAnimation } from "framer-motion";
import Index from "./pages/Index"; // Keep index for LCP
const Shop = lazy(() => import("./pages/Shop"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const Auth = lazy(() => import("./pages/Auth"));
const UpdatePassword = lazy(() => import("./pages/UpdatePassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Admin = lazy(() => import("./pages/Admin"));
const SellYourPhone = lazy(() => import("./pages/SellYourPhone"));
const Compare = lazy(() => import("./pages/Compare"));
const PassportView = lazy(() => import("./pages/PassportView"));
const PassportCertificate = lazy(() => import("./pages/PassportCertificate"));
const Marketplace = lazy(() => import("./pages/Marketplace"));
const MarketplaceListing = lazy(() => import("./pages/MarketplaceListing"));
const Messages = lazy(() => import("./pages/Messages"));
const RepairRequest = lazy(() => import("./pages/RepairRequest"));
const PhoneRecommendation = lazy(() => import("./pages/PhoneRecommendation"));
const IMEICheck = lazy(() => import("./pages/IMEICheck"));
const Staff = lazy(() => import("./pages/Staff"));
const StoreApplication = lazy(() => import("./pages/StoreApplication"));
const PartnerDashboard = lazy(() => import("./pages/PartnerDashboard"));
const Storefront = lazy(() => import("./pages/Storefront"));
const Pricing = lazy(() => import("./pages/Pricing"));
const PartnerDirectory = lazy(() => import("./pages/PartnerDirectory"));
const MyPassport = lazy(() => import("./pages/MyPassport"));
const VerifyPassport = lazy(() => import("./pages/VerifyPassport"));
const TripCheckout = lazy(() => import("./pages/TripCheckout"));
const Giveaways = lazy(() => import("./pages/Giveaways"));
const Ecosystem = lazy(() => import("./pages/Ecosystem"));
const NotFound = lazy(() => import("./pages/NotFound"));
import { WhatsAppFAB } from "./components/WhatsAppFAB";
import { PostHogProvider } from "./providers/PostHogProvider";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 1,         // Data is fresh for 1 minute
      gcTime: 1000 * 60 * 10,            // Keep cache for 10 minutes
      refetchOnWindowFocus: true,         // Refetch when user returns to tab
      refetchOnMount: 'always',           // Always refetch when component mounts
      retry: 2,                           // Retry failed fetches twice
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    },
    mutations: {
      retry: 1,
    },
  },
});

const MaintenanceGuard = ({ children }: { children: React.ReactNode }) => {
  const { enabled, message, hasBypass, loading: maintenanceLoading } = useMaintenanceMode();
  const { isAdmin, staffRole, isSuspended, isSuperAdmin, loading: authLoading } = useAuth();
  const location = useLocation();

  // Always allow admin & staff routes + auth page
  const alwaysAllowed = ["/admin", "/staff", "/auth"];
  const isAllowedRoute = alwaysAllowed.some((r) => location.pathname.startsWith(r));

  if (maintenanceLoading || authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen w-full bg-background" style={{ contain: 'content' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
          <p className="text-sm font-medium text-muted-foreground">Initializing system...</p>
        </div>
      </div>
    );
  }

  // Handle Suspension
  if (isSuspended && !isSuperAdmin && !location.pathname.startsWith("/auth")) {
    return (
      <div className="flex items-center justify-center min-h-screen w-full bg-slate-50">
        <div className="max-w-md w-full p-8 bg-white rounded-2xl shadow-xl border border-red-100 text-center animate-in fade-in zoom-in duration-300">
          <div className="w-20 h-20 bg-red-50 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <Ban className="w-10 h-10" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Account Suspended</h1>
          <p className="text-slate-500 mb-8 leading-relaxed">
            Your account has been suspended due to a violation of our terms of service or suspicious activity.
          </p>
          <div className="space-y-3">
            <Button className="w-full h-11 rounded-xl" onClick={() => window.location.href = "mailto:support@chawni.com"}>
              Contact Support
            </Button>
            <Button variant="ghost" className="w-full text-slate-400" onClick={() => window.location.reload()}>
              Retry Access
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Bypass maintenance if:
  // 1. Maintenance is OFF
  // 2. It's a system route (admin/staff/auth)
  // 3. User has a bypass key (URL param)
  // 4. User is an Admin or Staff member
  const shouldBypass = !enabled || isAllowedRoute || hasBypass || isAdmin || !!staffRole;

  if (shouldBypass) return <>{children}</>;

  return <MaintenancePage message={message} />;
};

const AnimatedRoutes = () => {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    // Debug mode: add ?debug=overflow to URL
    if (new URLSearchParams(location.search).get("debug") === "overflow") {
      document.documentElement.classList.add("debug-overflow");
    } else {
      document.documentElement.classList.remove("debug-overflow");
    }
  }, [location.pathname, location.search]);

  return (
    <MaintenanceGuard>
      <Suspense fallback={
        <div className="flex items-center justify-center min-h-screen w-full bg-background" style={{ contain: 'content' }}>
          <div className="flex flex-col items-center gap-4">
            <div className="h-12 w-12 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
            <p className="text-sm font-medium text-muted-foreground">Loading Chawni...</p>
          </div>
        </div>
      }>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/product/:slug" element={<ProductDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/update-password" element={<UpdatePassword />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/sell" element={<SellYourPhone />} />
          <Route path="/compare" element={<Compare />} />
          <Route path="/passport/:passportId" element={<PassportView />} />
          <Route path="/certificate/:passportId" element={<PassportCertificate />} />
          <Route path="/marketplace" element={<Marketplace />} />
          <Route path="/marketplace/:id" element={<MarketplaceListing />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/repair" element={<RepairRequest />} />
          <Route path="/recommend" element={<PhoneRecommendation />} />
          <Route path="/imei-check" element={<IMEICheck />} />
          <Route path="/staff" element={<Staff />} />
          <Route path="/partner/apply" element={<StoreApplication />} />
          <Route path="/partner/dashboard" element={<PartnerDashboard />} />
          <Route path="/store/:slug" element={<Storefront />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/partners" element={<PartnerDirectory />} />
          <Route path="/my-passport" element={<MyPassport />} />
          <Route path="/verify" element={<VerifyPassport />} />
          <Route path="/trip-checkout" element={<TripCheckout />} />
          <Route path="/giveaways" element={<Giveaways />} />
          <Route path="/ecosystem" element={<Ecosystem />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </MaintenanceGuard>
  );
};


const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
    <LazyMotion features={domAnimation}>
    <TooltipProvider>
      <ThemeProvider>
        <AuthProvider>
          <PostHogProvider>
            <ConfirmationProvider>
            <DemoProvider>
              <CartProvider>
                <WishlistProvider>
                  <Toaster />
                  <Sonner />
                  <BrowserRouter>
                    <AnimatedRoutes />
                    <WhatsAppFAB />
                    <Analytics />
                    <SpeedInsights />
                  </BrowserRouter>
                </WishlistProvider>
              </CartProvider>
            </DemoProvider>
            </ConfirmationProvider>
          </PostHogProvider>
        </AuthProvider>
      </ThemeProvider>
    </TooltipProvider>
    </LazyMotion>
    </QueryClientProvider>
  );
};

export default App;
